/**
 * Customer Locations View
 * Allows physicians to manage their practice locations and day-of-week availability
 * Part of Phase 3.5: Customer Self-Service Location Management
 */

import React, { useState, useEffect } from 'react';
import {
  MapPin, Plus, Edit3, Trash2, Save, X, CheckCircle, Building, Calendar
} from 'lucide-react';
import { customerLocationService, locationService } from '../services/locationService';
import AddLocationModal from '../components/AddLocationModal';
import { logger } from '../utils/logger';

const CustomerLocationsView = ({ currentUser }) => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [saveStatus, setSaveStatus] = useState('');

  // Days of week for availability editing
  const daysOfWeek = [
    { key: 'monday', label: 'Mon', fullLabel: 'Monday' },
    { key: 'tuesday', label: 'Tue', fullLabel: 'Tuesday' },
    { key: 'wednesday', label: 'Wed', fullLabel: 'Wednesday' },
    { key: 'thursday', label: 'Thu', fullLabel: 'Thursday' },
    { key: 'friday', label: 'Fri', fullLabel: 'Friday' },
    { key: 'saturday', label: 'Sat', fullLabel: 'Saturday' },
    { key: 'sunday', label: 'Sun', fullLabel: 'Sunday' }
  ];

  useEffect(() => {
    if (currentUser?.id) {
      loadCustomerLocations();
    } else {
      setLoading(false);
    }
  }, [currentUser]);

  const loadCustomerLocations = async () => {
    try {
      setLoading(true);
      const data = await customerLocationService.getCustomerLocations(currentUser.id);
      setLocations(data || []);
      logger.info('Customer locations loaded:', { count: data?.length || 0 });
    } catch (error) {
      logger.error('Error loading customer locations:', error);
      alert('Failed to load locations. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const handleLocationAdded = (newLocation) => {
    setShowAddModal(false);
    loadCustomerLocations(); // Reload to get the newly linked location
    setSaveStatus('Location added successfully!');
    setTimeout(() => setSaveStatus(''), 3000);
  };

  const handleEditLocation = (location) => {
    setEditingLocation({
      ...location,
      // Create editable copy of day-of-week flags
      works_monday: location.works_monday,
      works_tuesday: location.works_tuesday,
      works_wednesday: location.works_wednesday,
      works_thursday: location.works_thursday,
      works_friday: location.works_friday,
      works_saturday: location.works_saturday,
      works_sunday: location.works_sunday,
      is_primary_location: location.is_primary_location,
      role_at_location: location.role_at_location || '',
      department: location.department || '',
      // Editable practice_locations fields
      edited_location_name: location.practice_locations?.location_name || '',
      edited_address: location.practice_locations?.address || '',
      edited_city: location.practice_locations?.city || '',
      edited_state: location.practice_locations?.state || '',
      edited_zip: location.practice_locations?.zip || '',
      edited_phone: location.practice_locations?.phone || ''
    });
  };

  const handleCancelEdit = () => {
    setEditingLocation(null);
  };

  const handleSaveEdit = async () => {
    if (!editingLocation) return;

    try {
      // Update the customer_locations record (availability, role, department)
      await customerLocationService.updateCustomerLocation(editingLocation.id, {
        works_monday: editingLocation.works_monday,
        works_tuesday: editingLocation.works_tuesday,
        works_wednesday: editingLocation.works_wednesday,
        works_thursday: editingLocation.works_thursday,
        works_friday: editingLocation.works_friday,
        works_saturday: editingLocation.works_saturday,
        works_sunday: editingLocation.works_sunday,
        is_primary_location: editingLocation.is_primary_location,
        role_at_location: editingLocation.role_at_location,
        department: editingLocation.department
      });

      // Update the practice_locations record (name, address, etc.) if changed
      const locationId = editingLocation.location_id || editingLocation.practice_locations?.id;
      if (locationId) {
        await locationService.updateLocation(locationId, {
          location_name: editingLocation.edited_location_name,
          address: editingLocation.edited_address,
          city: editingLocation.edited_city,
          state: editingLocation.edited_state,
          zip: editingLocation.edited_zip,
          phone: editingLocation.edited_phone
        });
      }

      setSaveStatus('Location updated successfully!');
      setTimeout(() => setSaveStatus(''), 3000);
      setEditingLocation(null);
      loadCustomerLocations();
    } catch (error) {
      logger.error('Error updating location:', error);
      alert('Failed to update location. Please try again.');
    }
  };

  const handleRemoveLocation = async (location) => {
    const confirmed = window.confirm(
      `Remove ${location.practice_locations?.location_name} from your locations?\n\nThis will not delete the location, just unlink it from your profile.`
    );

    if (!confirmed) return;

    try {
      await customerLocationService.unlinkCustomerFromLocation(location.id);
      setSaveStatus('Location removed successfully!');
      setTimeout(() => setSaveStatus(''), 3000);
      loadCustomerLocations();
    } catch (error) {
      logger.error('Error removing location:', error);
      alert('Failed to remove location. Please try again.');
    }
  };

  const toggleDay = (day) => {
    if (!editingLocation) return;
    setEditingLocation({
      ...editingLocation,
      [`works_${day}`]: !editingLocation[`works_${day}`]
    });
  };

  const togglePrimaryLocation = () => {
    if (!editingLocation) return;
    setEditingLocation({
      ...editingLocation,
      is_primary_location: !editingLocation.is_primary_location
    });
  };

  const getActiveDays = (location) => {
    return daysOfWeek.filter(day => location[`works_${day.key}`]);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your practice locations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <Building className="w-8 h-8 mr-3 text-blue-600" />
              My Practice Locations
            </h1>
            <p className="text-gray-600 mt-2">
              Manage your practice locations and availability for each location
            </p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add Location
          </button>
        </div>
      </div>

      {/* Save Status */}
      {saveStatus && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center">
          <CheckCircle className="w-5 h-5 text-green-600 mr-3" />
          <span className="text-green-800">{saveStatus}</span>
        </div>
      )}

      {/* Empty State */}
      {locations.length === 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <MapPin className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            No practice locations yet
          </h3>
          <p className="text-gray-600 mb-6">
            Add your practice locations so sales reps can schedule meetings at the right place
          </p>
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add Your First Location
          </button>
        </div>
      )}

      {/* Locations List */}
      {locations.length > 0 && (
        <div className="space-y-4">
          {locations.map((location) => {
            const isEditing = editingLocation?.id === location.id;
            const practiceLocation = location.practice_locations;
            const activeDays = getActiveDays(isEditing ? editingLocation : location);

            return (
              <div
                key={location.id}
                className={`bg-white rounded-lg shadow-sm border-2 transition-all ${
                  isEditing ? 'border-blue-500' : 'border-gray-200'
                }`}
              >
                <div className="p-6">
                  {/* Location Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      {isEditing ? (
                        /* Editable Location Details */
                        <div className="space-y-3 mb-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Location Name
                            </label>
                            <input
                              type="text"
                              value={editingLocation.edited_location_name}
                              onChange={(e) =>
                                setEditingLocation({
                                  ...editingLocation,
                                  edited_location_name: e.target.value
                                })
                              }
                              placeholder="e.g., Main Office, Downtown Clinic"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-semibold"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Street Address
                            </label>
                            <input
                              type="text"
                              value={editingLocation.edited_address}
                              onChange={(e) =>
                                setEditingLocation({
                                  ...editingLocation,
                                  edited_address: e.target.value
                                })
                              }
                              placeholder="e.g., 123 Main St, Suite 100"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            />
                          </div>
                          <div className="grid grid-cols-3 gap-2">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                City
                              </label>
                              <input
                                type="text"
                                value={editingLocation.edited_city}
                                onChange={(e) =>
                                  setEditingLocation({
                                    ...editingLocation,
                                    edited_city: e.target.value
                                  })
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                State
                              </label>
                              <input
                                type="text"
                                value={editingLocation.edited_state}
                                onChange={(e) =>
                                  setEditingLocation({
                                    ...editingLocation,
                                    edited_state: e.target.value.toUpperCase()
                                  })
                                }
                                maxLength={2}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                ZIP Code
                              </label>
                              <input
                                type="text"
                                value={editingLocation.edited_zip}
                                onChange={(e) =>
                                  setEditingLocation({
                                    ...editingLocation,
                                    edited_zip: e.target.value
                                  })
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Phone (optional)
                            </label>
                            <input
                              type="tel"
                              value={editingLocation.edited_phone}
                              onChange={(e) =>
                                setEditingLocation({
                                  ...editingLocation,
                                  edited_phone: e.target.value
                                })
                              }
                              placeholder="(555) 123-4567"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            />
                          </div>
                        </div>
                      ) : (
                        /* Read-only Location Details */
                        <>
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-xl font-bold text-gray-900">
                              {practiceLocation?.location_name || 'Unnamed Location'}
                            </h3>
                            {location.is_primary_location && (
                              <span className="px-3 py-1 bg-purple-100 text-purple-800 text-xs font-semibold rounded-full">
                                Primary Location
                              </span>
                            )}
                            {practiceLocation?.location_type && (
                              <span className="px-3 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded-full">
                                {practiceLocation.location_type.replace('_', ' ')}
                              </span>
                            )}
                          </div>

                          {/* Address */}
                          <div className="text-gray-600 text-sm space-y-1">
                            <div className="flex items-center">
                              <MapPin className="w-4 h-4 mr-2" />
                              {practiceLocation?.address}
                            </div>
                            <div className="ml-6">
                              {practiceLocation?.city}, {practiceLocation?.state} {practiceLocation?.zip}
                            </div>
                            {practiceLocation?.phone && (
                              <div className="ml-6 text-gray-500">
                                Phone: {practiceLocation.phone}
                              </div>
                            )}
                          </div>
                        </>
                      )}

                      {/* Role/Department (in edit mode or if set) */}
                      {(isEditing || location.role_at_location || location.department) && (
                        <div className="mt-3 text-sm text-gray-600">
                          {isEditing ? (
                            <div className="flex gap-4">
                              <div className="flex-1">
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Your Role
                                </label>
                                <input
                                  type="text"
                                  value={editingLocation.role_at_location}
                                  onChange={(e) =>
                                    setEditingLocation({
                                      ...editingLocation,
                                      role_at_location: e.target.value
                                    })
                                  }
                                  placeholder="e.g., Attending Physician"
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                />
                              </div>
                              <div className="flex-1">
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Department
                                </label>
                                <input
                                  type="text"
                                  value={editingLocation.department}
                                  onChange={(e) =>
                                    setEditingLocation({
                                      ...editingLocation,
                                      department: e.target.value
                                    })
                                  }
                                  placeholder="e.g., Cardiology"
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                />
                              </div>
                            </div>
                          ) : (
                            <>
                              {location.role_at_location && (
                                <span>Role: {location.role_at_location}</span>
                              )}
                              {location.role_at_location && location.department && (
                                <span className="mx-2">•</span>
                              )}
                              {location.department && (
                                <span>Department: {location.department}</span>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    {!isEditing && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditLocation(location)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit availability"
                        >
                          <Edit3 className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleRemoveLocation(location)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Remove location"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Day-of-Week Availability */}
                  <div className="border-t border-gray-200 pt-4">
                    <div className="flex items-center mb-3">
                      <Calendar className="w-4 h-4 text-gray-500 mr-2" />
                      <span className="text-sm font-medium text-gray-700">
                        Days Available at This Location
                      </span>
                    </div>

                    {isEditing ? (
                      <div className="space-y-4">
                        <div className="grid grid-cols-7 gap-2">
                          {daysOfWeek.map((day) => (
                            <button
                              key={day.key}
                              onClick={() => toggleDay(day.key)}
                              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                                editingLocation[`works_${day.key}`]
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                            >
                              {day.label}
                            </button>
                          ))}
                        </div>

                        {/* Primary Location Toggle */}
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id={`primary-${location.id}`}
                            checked={editingLocation.is_primary_location}
                            onChange={togglePrimaryLocation}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <label
                            htmlFor={`primary-${location.id}`}
                            className="ml-2 text-sm text-gray-700"
                          >
                            Set as primary location
                          </label>
                        </div>

                        {/* Save/Cancel Buttons */}
                        <div className="flex gap-3">
                          <button
                            onClick={handleSaveEdit}
                            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                          >
                            <Save className="w-4 h-4 mr-2" />
                            Save Changes
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                          >
                            <X className="w-4 h-4 mr-2" />
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {activeDays.length > 0 ? (
                          activeDays.map((day) => (
                            <span
                              key={day.key}
                              className="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full"
                            >
                              {day.label}
                            </span>
                          ))
                        ) : (
                          <span className="text-sm text-gray-500 italic">
                            No days selected - click Edit to set availability
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add Location Modal */}
      <AddLocationModal
        customerId={currentUser?.id}
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onLocationAdded={handleLocationAdded}
      />

      {/* Info Box */}
      <div className="mt-8 p-6 bg-blue-50 border border-blue-200 rounded-lg">
        <h4 className="font-semibold text-blue-900 mb-2">
          How Location Availability Works
        </h4>
        <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
          <li>
            Sales reps will see which locations you work at when booking meetings
          </li>
          <li>
            The calendar will only show days when you're available at the selected location
          </li>
          <li>
            You can work different days at different locations (e.g., Mon-Wed at Hospital A, Thu-Fri at Clinic B)
          </li>
          <li>
            Your primary location will be suggested first when scheduling meetings
          </li>
        </ul>
      </div>
    </div>
  );
};

export default CustomerLocationsView;
